import React, { FC, ReactNode } from 'react';

import { useTheme } from 'styled-components';

import { BaseStyleEnums } from '@nhl/nhl-breakaway-ui';

import { useIsWebview } from '~/core/hooks/useIsWebview';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';

import { LandingGame, Player } from '../../../../types';
import ProgressBar from '../../../progressBar/ProgressBar';
import { PlayerHeadshot, PlayerInfo } from '../../utilityComponents';

import {
    DetailLabel,
    DetailsContainer,
    PlayerCompareContainer,
    PlayerContainer,
    SmallPlayerInfoContainer,
    StatValue,
} from './styles';

type Props = {
    accessorField: string;
    accessorLabel: string;
    awayLeader: Player;
    homeLeader: Player;
    game: LandingGame;
    getDisplayValue?: (value: number | string | undefined) => ReactNode;
    title: ReactNode;
};

const PlayerCompare: FC<Props> = ({ awayLeader, homeLeader, accessorField, game, getDisplayValue, title }) => {
    const isWebview = useIsWebview();
    const theme = useTheme();
    const size = useSizeWithThrottle();
    const isMedium = size.width >= BaseStyleEnums.ScreenWidthBreakpoints.medium;

    let awayValue = awayLeader[accessorField];
    let homeValue = homeLeader[accessorField];
    if (getDisplayValue) {
        awayValue = getDisplayValue(awayValue);
        homeValue = getDisplayValue(homeValue);
    }

    return (
        <PlayerCompareContainer>
            <DetailsContainer>
                <PlayerContainer>
                    <PlayerHeadshot
                        isWebview={isWebview}
                        leader={awayLeader}
                        backgroundColor={theme.Team[game.awayTeam.abbrev]?.gamecenter}
                    />
                    {isMedium && <PlayerInfo isWebview={isWebview} leader={awayLeader} />}
                    <StatValue>{awayValue}</StatValue>
                </PlayerContainer>
                <DetailLabel>{title}</DetailLabel>
                <PlayerContainer>
                    <StatValue>{homeValue}</StatValue>
                    {isMedium && <PlayerInfo isRightAligned isWebview={isWebview} leader={homeLeader} />}
                    <PlayerHeadshot
                        isWebview={isWebview}
                        leader={homeLeader}
                        backgroundColor={theme.Team[game.homeTeam.abbrev]?.gamecenter}
                    />
                </PlayerContainer>
            </DetailsContainer>
            <ProgressBar
                awayTeamColor={theme.Team[game.awayTeam.abbrev]?.gamecenter || theme.Surface.Opposite.tertiary}
                awayTeamValue={awayValue}
                homeTeamColor={theme.Team[game.homeTeam.abbrev]?.gamecenter || theme.Surface.Opposite.tertiary}
                homeTeamValue={homeValue}
            />
            {!isMedium && (
                <SmallPlayerInfoContainer>
                    <PlayerInfo isWebview={isWebview} leader={awayLeader} />
                    <PlayerInfo isRightAligned isWebview={isWebview} leader={homeLeader} />
                </SmallPlayerInfoContainer>
            )}
        </PlayerCompareContainer>
    );
};

export default PlayerCompare;
